// Slack Integration Service
// This service handles communication with the server's Slack API

import axios from 'axios';

class SlackService {
  constructor() {
    this.isConnected = false;
    this.isMockMode = false;
    this.messageListeners = [];
    this.hasMessageError = false; // Track if messages API has errors
  }

  // Initialize connection to Slack via server
  async initialize() {
    try {
      const response = await axios.get('/api/slack/test');
      this.isConnected = response.data.ok;
      this.isMockMode = response.data.isMockMode;
      this.hasMessageError = false;
      return this.isConnected;
    } catch (error) {
      this.isConnected = false;
      this.isMockMode = true;
      this.hasMessageError = true;
      return false;
    }
  }

  // Send message to Slack channel via server
  async sendMessage(text, user) {
    try {
      const response = await axios.post('/api/slack/send', {
        text,
      });

      if (response.data.success) {
        if (response.data.isMockMode && this.isMockMode) {
          // In mock mode, simulate a response from support team
          setTimeout(
            () => {
              this.notifyMessageListeners({
                id: Date.now(),
                user: 'Support Team',
                text: this.generateMockResponse(text),
                timestamp: new Date(),
                type: 'support',
              });
            },
            2000 + Math.random() * 3000
          ); // Random delay between 2-5 seconds
        }

        return {
          success: true,
          timestamp: response.data.timestamp,
          message: text,
        };
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || error.message,
      };
    }
  }

  // Generate mock responses based on message content
  generateMockResponse(message) {
    const responses = {
      greeting: [
        'Hi there! How can I help you today?',
        "Hello! I'm here to assist you with any questions.",
        'Hey! What can I help you with?',
      ],
      problem: [
        "I understand you're having an issue. Can you provide more details?",
        "Sorry to hear you're experiencing problems. Let me help you resolve this.",
        "I'll look into that for you right away.",
      ],
      question: [
        "That's a great question! Let me get you the information you need.",
        "I can help you with that. Here's what you need to know...",
        "Thanks for asking! I'll provide you with the details.",
      ],
      thanks: [
        "You're welcome! Is there anything else I can help you with?",
        'Happy to help! Feel free to reach out if you need anything else.',
        'Glad I could assist! Let me know if you have other questions.',
      ],
      default: [
        "Thanks for your message! I'll get back to you shortly with more information.",
        "I've received your message and will respond as soon as possible.",
        "Let me look into that for you. I'll have an answer shortly.",
      ],
    };

    const lowerMessage = message.toLowerCase();

    if (
      lowerMessage.includes('hello') ||
      lowerMessage.includes('hi') ||
      lowerMessage.includes('hey')
    ) {
      return this.getRandomResponse(responses.greeting);
    } else if (
      lowerMessage.includes('problem') ||
      lowerMessage.includes('issue') ||
      lowerMessage.includes('error') ||
      lowerMessage.includes('bug')
    ) {
      return this.getRandomResponse(responses.problem);
    } else if (
      lowerMessage.includes('?') ||
      lowerMessage.includes('how') ||
      lowerMessage.includes('what') ||
      lowerMessage.includes('when')
    ) {
      return this.getRandomResponse(responses.question);
    } else if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return this.getRandomResponse(responses.thanks);
    } else {
      return this.getRandomResponse(responses.default);
    }
  }

  getRandomResponse(responses) {
    return responses[Math.floor(Math.random() * responses.length)];
  }

  // Get recent messages from Slack channel via server
  async getRecentMessages(limit = 50) {
    try {
      const response = await axios.get('/api/slack/messages', {
        params: { limit },
      });

      // Check if the response indicates an error (like missing_scope)
      if (response.data?.error) {
        this.hasMessageError = true;
        this.isMockMode = true;
      } else {
        this.hasMessageError = false;
      }

      // Ensure we always return an array
      return response.data?.messages || this.getMockMessages();
    } catch (error) {
      // Mark that messages have errors so polling stops
      this.hasMessageError = true;
      this.isMockMode = true;
      // Silently return mock messages on error (avoid console spam)
      return this.getMockMessages();
    }
  }

  // Get mock messages for demo
  getMockMessages() {
    return [
      {
        id: 1,
        user: 'System',
        text: 'Welcome to Point of Rental Data Portal support chat! 🎉',
        timestamp: new Date(Date.now() - 3600000),
        type: 'system',
      },
      {
        id: 2,
        user: 'Support Team',
        text: "Hi! I'm here to help you with any questions about the Data Portal. How can I assist you today?",
        timestamp: new Date(Date.now() - 1800000),
        type: 'support',
      },
    ];
  }

  // Add message listener for real-time updates
  addMessageListener(callback) {
    this.messageListeners.push(callback);
  }

  // Remove message listener
  removeMessageListener(callback) {
    this.messageListeners = this.messageListeners.filter((listener) => listener !== callback);
  }

  // Notify all listeners of new messages
  notifyMessageListeners(message) {
    this.messageListeners.forEach((callback) => callback(message));
  }

  // Get connection status
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      isMockMode: this.isMockMode,
      hasToken: !this.isMockMode && !this.hasMessageError, // Only true when working properly
    };
  }

  // Disconnect from Slack
  disconnect() {
    this.isConnected = false;
    this.messageListeners = [];
  }
}

// Export singleton instance
export default new SlackService();
